import { DEFAULT_PAGE_SIZE } from '@/lib/constants/pagination';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import { mapShortProjectsToDropdownItems } from '../mappers/project-short.mapper';
import {
  FindShortProjectsFilterRequest,
  FindShortProjectsRequest,
} from '../types/dto/request/find-short-projects-request';
import { ShortProjectResponse } from '../types/dto/response/short-project-response';

/**
 * Fetches short projects for the organization with cursor pagination and filters.
 * Used for dropdowns and lists where full project details are not needed.
 *
 * @param params - Optional filter and pagination parameters
 * @returns Paginated list of short project information as DropdownSearchItem
 */
export const getShortProjects = async (params?: {
  filter?: FindShortProjectsFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  const endpoint = '/api/projects/short/search';

  const requestBody: FindShortProjectsRequest = {
    filter: params?.filter,
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      cursor: params?.pagination?.cursor,
    },
  };

  const response = await axiosInstance.post<{
    data: {
      items: ShortProjectResponse[];
      pagination: {
        total: number;
        cursor: number | null;
      };
    };
    error?: { status: number; message: string; code: string };
  }>(endpoint, requestBody);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  const apiData = response.data.data;

  return new PaginatedResponse({
    items: mapShortProjectsToDropdownItems(apiData.items),
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Same as getShortProjects but returns raw ShortProjectResponse items
 * without mapping to DropdownSearchItem, preserving fields like
 * connectedIntegrations, currencyCode, and clientName.
 */
export const getShortProjectsRaw = async (params?: {
  filter?: FindShortProjectsFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<ShortProjectResponse>> => {
  const endpoint = '/api/projects/short/search';

  const requestBody: FindShortProjectsRequest = {
    filter: params?.filter,
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      cursor: params?.pagination?.cursor,
    },
  };

  const response = await axiosInstance.post<{
    data: {
      items: ShortProjectResponse[];
      pagination: {
        total: number;
        cursor: number | null;
      };
    };
    error?: { status: number; message: string; code: string };
  }>(endpoint, requestBody);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  const apiData = response.data.data;

  return new PaginatedResponse({
    items: apiData.items,
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};
